import {
  Alert,
  DialogStep,
  formatAddress,
  TextFieldSplit,
} from "@inductiveautomation/ws-components";
import { useTranslation } from "react-i18next";
import { useAddressWithFullStateName } from "../../../../hooks/useStateNameLookup";

import { useCurrentUser } from "../../../../hooks/currentUser";
import { getDialogStyles } from "../../_training.styles";
import { PurchaseTestReviewStepProps } from "../../Training.types";

export const useConfirmationStep = ({
  isOpen,
  printButton,
  onClose,
  purchaseBarProps,
  values,
  purchaseResponse,
  purchaseAlert,
  certificationType,
  creditsAvailable,
  paymentMethod,
}: PurchaseTestReviewStepProps): DialogStep => {
  const translationKeyPrefix =
    certificationType?.toLowerCase() === "gold"
      ? "training.actions.purchase_test.dialog.gold.steps.order_complete"
      : "training.actions.purchase_test.dialog.core.steps.order_complete";
  const { t } = useTranslation("translation", {
    keyPrefix: translationKeyPrefix,
  });
  const { classes } = getDialogStyles();
  const addressWithStateName = useAddressWithFullStateName(
    values.billingAddress
  );
  const { user } = useCurrentUser();

  return {
    label: t("label"),
    substeps: [],
    hideSidebar: true,
    dialogProps: {
      isOpen,
      printButton,
      testId: "purchase-test-dialog-review_order",
      showCloseButton: true,
      onClose,
      overline: "",
      headline: t("heading"),
      supportingText: purchaseResponse?.data?.orderNumber
        ? t("supporting_text_with_order_number", {
            orderNumber: `#[${purchaseResponse.data.orderNumber}]`,
          })
        : t("supporting_text_no_order_number"),
      contentArea: (
        <>
          <div className={classes.reviewOrderAlertContainer}>
            {purchaseAlert?.description ? (
              <Alert
                id="purchase-test-dialog-review_order-alert"
                variant={purchaseAlert.variant ?? "neutral"}
                title={purchaseAlert.title}
                description={purchaseAlert.description}
                closeButtonEventHandler={() => undefined}
              />
            ) : (
              <Alert
                id="purchase-test-dialog-review_order-alert"
                variant="info"
                description={t("alert_text")}
                closeButtonEventHandler={() => undefined}
              />
            )}
          </div>

          <div className={classes.reviewOrderFieldsContainer}>
            <TextFieldSplit
              id="purchase-test-dialog-review_order-products"
              label={t("products_label")}
              isReadOnly
              value={t("products_value")}
              onChange={() => undefined}
              onChipChange={() => undefined}
              items={[]}
              hasButton
              disabled
              className={classes.reviewOrderTextFieldSplit}
              helperMessage={new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(purchaseResponse?.data?.retailTotal ?? 0)}
            />
            {(!creditsAvailable?.length ||
              paymentMethod === "DO_NOT_USE_CREDIT") && (
              <>
                <TextFieldSplit
                  id="purchase-test-dialog-review_order-personal_information"
                  label={t("personal_information_label")}
                  isReadOnly
                  value={`${user?.firstName} ${user?.lastName}`}
                  onChange={() => undefined}
                  onChipChange={() => undefined}
                  items={[]}
                  hasButton
                  disabled
                  className={classes.reviewOrderTextFieldSplit}
                />
                <TextFieldSplit
                  id="purchase-test-dialog-review_order-credit_card"
                  label={t("credit_card_label")}
                  isReadOnly
                  value={`**** **** **** ${purchaseResponse?.data?.ccLast4}`}
                  onChange={() => undefined}
                  onChipChange={() => undefined}
                  items={[]}
                  hasButton
                  disabled
                  className={classes.reviewOrderTextFieldSplit}
                />
                <TextFieldSplit
                  id="purchase-test-dialog-review_order-biling_address"
                  label={t("billing_address_label")}
                  isReadOnly
                  value={
                    addressWithStateName
                      ? formatAddress(addressWithStateName)
                      : "–"
                  }
                  onChange={() => undefined}
                  onChipChange={() => undefined}
                  items={[]}
                  hasButton
                  disabled
                  className={classes.reviewOrderTextFieldSplit}
                />
              </>
            )}
          </div>
        </>
      ),
      purchaseBarProps,
    },
  };
};
